import React from 'react';
import { Users, TrendingUp, Target, Mail, BarChart3, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';

const Advertise = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              <span className="text-gradient">Advertise With Us</span>
            </h1>
            <p className="text-xl text-gray-300">
              Reach a targeted audience of trucking professionals focused on safety, compliance, and risk management.
            </p>
          </div>
        </div>
      </section>

      {/* Our Audience Section */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Audience</h2>
            <p className="text-lg text-gray-300 text-center mb-12 max-w-3xl mx-auto">
              Your message will reach decision-makers and professionals actively seeking solutions 
              for safety, compliance, and fleet management.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Users,
                  title: 'Professional Truck Drivers',
                  description: 'Company drivers and owner-operators'
                },
                {
                  icon: Target,
                  title: 'Fleet Owners & Managers',
                  description: 'Decision-makers for fleet operations'
                },
                {
                  icon: BarChart3,
                  title: 'Safety Directors',
                  description: 'Responsible for compliance and training'
                },
                {
                  icon: TrendingUp,
                  title: 'Industry Service Providers',
                  description: 'Seeking partnership opportunities'
                }
              ].map((item, index) => (
                <Card key={index} className="bg-gray-800 border-gray-700 text-center">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mx-auto mb-3">
                      <item.icon className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white text-lg">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">{item.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advertising Options */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Advertising Options</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Header Banner Ads',
                  description: 'Premium visibility at the top of every page. Maximum exposure for your brand.',
                  features: ['Above-the-fold placement', 'High visibility', 'All pages or targeted sections']
                },
                {
                  title: 'Sidebar Ads',
                  description: 'Persistent presence alongside content throughout the user journey.',
                  features: ['Always visible while scrolling', 'Multiple size options', 'Category targeting']
                },
                {
                  title: 'In-Article Placements',
                  description: 'Native advertising within relevant blog posts and articles.',
                  features: ['Contextual relevance', 'Higher engagement', 'Content integration']
                },
                {
                  title: 'Sponsored Content',
                  description: 'Publish educational content that showcases your expertise and solutions.',
                  features: ['Full article format', 'Lasting value', 'Brand authority building']
                }
              ].map((option, index) => (
                <Card key={index} className="bg-gray-800 border-gray-700 card-hover">
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{option.title}</CardTitle>
                    <CardDescription className="text-gray-400">{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {option.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center space-x-2 text-gray-300 text-sm">
                          <div className="h-1.5 w-1.5 rounded-full bg-orange-400"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Advertise Here */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Why Advertise Here</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Target,
                  title: 'Highly Targeted Niche Audience',
                  description: 'Reach professionals actively seeking safety and compliance solutions, not casual browsers.'
                },
                {
                  icon: Users,
                  title: 'Safety & Compliance-Focused Visitors',
                  description: 'Our audience is motivated to improve operations and reduce risk—ideal for relevant products.'
                },
                {
                  icon: FileText,
                  title: 'Long-Form Educational Content',
                  description: 'Higher engagement times mean more exposure for your advertising message.'
                },
                {
                  icon: TrendingUp,
                  title: 'Growing Email Subscriber Base',
                  description: 'Extended reach through our newsletter that targets engaged subscribers.'
                }
              ].map((benefit, index) => (
                <Card key={index} className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-3">
                      <benefit.icon className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">{benefit.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-2xl p-8 md:p-12 text-center">
              <Mail className="h-12 w-12 text-orange-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Reach Our Audience?
              </h2>
              <p className="text-gray-300 mb-6 text-lg">
                Contact us to request our media kit and learn more about advertising rates and opportunities.
              </p>
              <Button size="lg" className="btn-primary text-white font-semibold px-8 h-12" asChild>
                <Link to="/contact">
                  Request Media Kit
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Advertise;
